import { Button } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../../../utils/validation';
import { CardioPerfectEthnicGroup } from '../../../../../../common/enums/cardioPerfectEthnicGroup';
import { CareFusionEthnicGroup } from '../../../../../../common/enums/careFusionEthnicGroup';
import { GenderSpirometer } from '../../../../../../common/enums/genderSpirometer';
import { SpirometerSoftwareNames } from '../../../../../../common/enums/spirometerSoftwareNames';
import {
    cardioPerfectSchema,
    careFusionSchema,
    rokerDuurOpties,
    rookgedragOptieRoker,
    rookgedragOpties,
} from '../../../../../../common/validation/customControls/pulmonology';
import { translateKey } from '../../../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../../utils/validation';
import { Dropdown, EnumDropdown, InputNumber, InputRadioGroup } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { Modal } from '../../../../elements/modals/Modal';

interface ISpirogramDataProps {
    onClose: () => Promise<void>;
    setSpirogramData: (item: ISpirogramData) => Promise<void>;
    spirogramData: ISpirogramData;
    spirometerSoftwareName?: SpirometerSoftwareNames;
}

export interface ISpirogramData {
    cardioPerfectEthnicGroup?: CardioPerfectEthnicGroup | null;
    careFusionEthnicGroup?: CareFusionEthnicGroup | null;
    gender: GenderSpirometer | null;
    gewicht: number | null;
    lengte: number | null;
    rokerDuur?: string | null;
    rookgedrag?: string | null;
}

interface ISpirogramDataState {
    errors: ValidationErrors;
    spirogramData: ISpirogramData;
}

export class SpirogramData extends Component<ISpirogramDataProps, ISpirogramDataState> {
    constructor(props: ISpirogramDataProps, context?: {}) {
        super(props, context);
        this.state = {
            spirogramData: {
                ...this.props.spirogramData,
            },
            errors: emptyValidationErrors,
        };
    }

    public render() {
        return (
            <Modal
                actionButtons={[
                    <Button
                        onClick={async () => await this.validateSpirogramData()}
                        variant="solid"
                    >
                        {translateKey('common.proceed')}
                    </Button>,
                ]}
                isOpen
                onClose={async () => await this.props.onClose()}
                size="medium"
                title="Start spirometer"
            >
                <HorizontalFormGroup hasErrors={this.state.errors.has('lengte')}>
                    <FormLabel isRequired>{translateKey('common.length')}</FormLabel>
                    <InputContainer size="medium">
                        <InputNumber
                            error={this.state.errors.get('lengte')}
                            onChange={async (value) => await this.handleChange('lengte', value)}
                            onDoValidate={async () => await this.validateOne('lengte')}
                            placeholder={translateKey('common.length') + ' (cm)'}
                            value={this.state.spirogramData.lengte}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
                <HorizontalFormGroup hasErrors={this.state.errors.has('gewicht')}>
                    <FormLabel isRequired>{translateKey('common.weight')}</FormLabel>
                    <InputContainer size="medium">
                        <InputNumber
                            error={this.state.errors.get('gewicht')}
                            onChange={async (value) => await this.handleChange('gewicht', value)}
                            onDoValidate={async () => await this.validateOne('gewicht')}
                            placeholder={translateKey('common.weight') + ' (kg)'}
                            value={this.state.spirogramData.gewicht}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
                {this.props.spirometerSoftwareName === SpirometerSoftwareNames.CareFusion && (
                    <>
                        <HorizontalFormGroup hasErrors={this.state.errors.has('careFusionEthnicGroup')}>
                            <FormLabel isRequired>{translateKey('common.ethnicity')}</FormLabel>
                            <InputContainer>
                                <EnumDropdown
                                    enumName="careFusionEthnicGroup"
                                    error={this.state.errors.get('careFusionEthnicGroup')}
                                    jsEnum={CareFusionEthnicGroup}
                                    onChange={async (value) => await this.handleChange('careFusionEthnicGroup', value)}
                                    onDoValidate={async () => await this.validateOne('careFusionEthnicGroup')}
                                    placeholder={translateKey('common.ethnicity')}
                                    value={
                                        this.state.spirogramData.careFusionEthnicGroup
                                            ? this.state.spirogramData.careFusionEthnicGroup
                                            : null
                                    }
                                />
                            </InputContainer>
                        </HorizontalFormGroup>
                        <HorizontalFormGroup hasErrors={this.state.errors.has('rookgedrag')}>
                            <FormLabel isRequired>Roker</FormLabel>
                            <InputRadioGroup
                                error={this.state.errors.get('rookgedrag')}
                                name={translateKey('common.smoker')}
                                onChange={async (value) => {
                                    if (value !== rookgedragOptieRoker) {
                                        await this.setState(({ spirogramData }) => ({
                                            spirogramData: { ...spirogramData, rokerDuur: undefined },
                                        }));
                                    }
                                    await this.handleChange('rookgedrag', value);
                                }}
                                onDoValidate={async () => await this.validateOne('rookgedrag')}
                                options={rookgedragOpties}
                                value={this.state.spirogramData.rookgedrag || null}
                            />
                        </HorizontalFormGroup>
                    </>
                )}
                {this.state.spirogramData.rookgedrag === rookgedragOptieRoker && (
                    <>
                        <HorizontalFormGroup hasErrors={this.state.errors.has('rokerDuur')}>
                            <FormLabel isRequired>Hoeveelheid jaren gerookt</FormLabel>
                            <Dropdown
                                error={this.state.errors.get('rokerDuur')}
                                onChange={async (value) => await this.handleChange('rokerDuur', value)}
                                onDoValidate={async () => await this.validateOne('rokerDuur')}
                                placeholder={translateKey('common.smokingDuration')}
                                selectOptions={rokerDuurOpties.map((rokerDuurOptie) => ({
                                    value: rokerDuurOptie,
                                    text: rokerDuurOptie,
                                }))}
                                value={this.state.spirogramData.rokerDuur || null}
                            />
                        </HorizontalFormGroup>
                    </>
                )}
                {this.props.spirometerSoftwareName === SpirometerSoftwareNames.CardioPerfect && (
                    <HorizontalFormGroup hasErrors={this.state.errors.has('cardioPerfectEthnicGroup')}>
                        <FormLabel isRequired>{translateKey('common.ethnicity')}</FormLabel>
                        <InputContainer>
                            <EnumDropdown
                                enumName="cardioPerfectEthnicGroup"
                                error={this.state.errors.get('cardioPerfectEthnicGroup')}
                                jsEnum={CardioPerfectEthnicGroup}
                                onChange={async (value) => await this.handleChange('cardioPerfectEthnicGroup', value)}
                                onDoValidate={async () => await this.validateOne('cardioPerfectEthnicGroup')}
                                placeholder={translateKey('common.ethnicity')}
                                value={
                                    this.state.spirogramData.cardioPerfectEthnicGroup
                                        ? this.state.spirogramData.cardioPerfectEthnicGroup
                                        : null
                                }
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                )}

                {!this.props.spirogramData.gender && (
                    <HorizontalFormGroup hasErrors={this.state.errors.has('gender')}>
                        <FormLabel isRequired>{translateKey('common.gender')}</FormLabel>
                        <InputContainer size="medium">
                            <EnumDropdown
                                enumName="genderSpirometer"
                                error={this.state.errors.get('gender')}
                                jsEnum={GenderSpirometer}
                                onChange={async (value) => await this.handleChange('gender', value)}
                                onDoValidate={async () => await this.validateOne('gender')}
                                placeholder={translateKey('common.gender')}
                                value={this.state.spirogramData.gender}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                )}
            </Modal>
        );
    }

    private async validateSpirogramData() {
        const { hasErrors, errors, item } = validate(this.getSchema(), this.state.spirogramData);
        await this.setState({ errors });
        if (!hasErrors && item) {
            this.props.setSpirogramData(item);
        }
    }

    private getSchema() {
        if (this.props.spirometerSoftwareName === SpirometerSoftwareNames.CardioPerfect) {
            return cardioPerfectSchema;
        } else if (this.props.spirometerSoftwareName === SpirometerSoftwareNames.CareFusion) {
            return careFusionSchema;
        } else {
            throw new Error('Unknown Spirometer software');
        }
    }

    private async handleChange<K extends keyof ISpirogramData>(name: K, value: ISpirogramData[K]) {
        await this.setState(({ spirogramData }) => ({ spirogramData: { ...spirogramData, [name]: value } }));
    }

    private async validateOne(name: string) {
        await this.setState(({ spirogramData, errors }) => ({
            errors: updateValidationErrorsForOne(this.getSchema(), spirogramData, errors, name),
        }));
    }
}
